import React, { useEffect } from 'react';
import * as i18next from 'i18next';
import ConsentStatus from '../../domain/enums/ConsentStatus';

interface IProps {
  t: i18next.TFunction;
  bankName: string;
  oldConsent: Date;
  consentStatus?: ConsentStatus;
  isImplicit?: boolean;
}

const getActiveOrExpiredConsentText = (t: i18next.TFunction, consentStatus?: ConsentStatus) => {
  if (consentStatus === ConsentStatus.Expired || consentStatus === ConsentStatus.Revoked) {
    return `${t<string>('Your current shared access expired on ')}`;
  }

  return `${t<string>('Your current shared access will expire on ')}`;
};

const getSharingInformation = (t: i18next.TFunction, oldConsent?: Date) => {
  if (!oldConsent) {
    return `${t<string>('In order to continue using shared data, you will need to reconnect your account.')}`;
  }

  return `${t<string>(
    'In order to keep your data safe, we have to periodically check to make sure you still want to share your information.'
  )}`;
};

const getTitle = (t: i18next.TFunction, bankName: string, isImplicit: boolean | undefined) => {
  if (isImplicit) {
    return `${t<string>('Connect your {{bankName}} account', {
      bankName: bankName
    })}`;
  }

  return `${t<string>('Consent to share financial information')}`;
};

const ReauthenticateConsentPageContent: React.FC<IProps> = (props: IProps) => {
  const { t, bankName, oldConsent, consentStatus, isImplicit } = props;
  const sharingInformation = getSharingInformation(t, oldConsent);
  const activeOrExpiredConsentText = getActiveOrExpiredConsentText(t, consentStatus);
  const title = getTitle(t, bankName, isImplicit);

  useEffect(() => {
    if (window.hj) {
      window.hj('tagRecording', ['Screen: Reauthentication Consent']);
    }
  }, []);

  return (
    <div data-testid="reauthenticate-consent-page-content">
      <div className="mb-6">
        <h1 className="text-md md:text-lg text-primary-2 uppercase font-bold ">{title}</h1>
        <p className={`text-md`}>{sharingInformation}</p>
        <p className={`text-md`}>{t<string>('We’ll aim to make this as quick and easy as possible.')}</p>
        {!oldConsent || (
          <p>
            {activeOrExpiredConsentText}
            <strong>
              {oldConsent.toLocaleDateString(navigator.language, {
                year: 'numeric',
                month: 'long',
                day: '2-digit'
              })}
            </strong>
          </p>
        )}
      </div>
    </div>
  );
};

export default ReauthenticateConsentPageContent;
